import React from 'react';
import cn from 'classnames';

import {
	Button, Display, QuickTypes, Sizes,
} from 'Views/Components/Button/Button';
import { Conditional } from 'Views/Components/Conditional/Conditional';

interface IRecursiveAccordion<ListItem> {
	// id of the resource being used for the accordion
	id: string;
	// title shown in the accordion header bar
	title: string;
	/**
	 * current recursive tree depth used to determine whether
	 * accordion overlaps with parent in styling
	 */
	depth: number;
	// current collapsed state of accordion
	collapsed: boolean;
	// callback toggle to alter the collapsed state
	setCollapsed: (id: string, newState?: boolean) => any;
	// list of child items to be rendered
	items?: ListItem[],
	// callback user to render a child component
	renderItem: (item: ListItem, index: number) => React.ReactNode;

	unitCount?: number;
	unitMinimum?: number;
	emptyLabel: string;
	description?: React.ReactNode;
	errorMessage?: React.ReactNode;
	additionalGroupHeaderItems?: React.ReactNode;
}

const CourseAccordion = <ListItem, >({
	id,
	title,
	depth,
	collapsed,
	setCollapsed,
	items,
	renderItem,
	unitCount,
	unitMinimum,
	emptyLabel,
	description,
	errorMessage,
	additionalGroupHeaderItems,
}: IRecursiveAccordion<ListItem>) => {
	return (
		<div
			className={
				cn(
					'course-accordion',
					`depth-${depth}`,
					{
						collapsed,
						'top-level': depth === 0,
					},
				)
			}
		>
			<div
				className={cn(
					'group-header',
					`depth-${depth}`,
					'overlap-item',
					'overlappable-item',
				)}
			>
				<span className="group-header__title">
					{title}
				</span>
				<span className="group-header__right">
					<Conditional
						condition={additionalGroupHeaderItems}
						component={() => (
							<>{additionalGroupHeaderItems}</>
						)}
						componentProps={{}}
					/>
					<Conditional
						condition={unitCount}
						component={
							() => (
								<span className="unit-count">
									{unitCount}{unitMinimum !== undefined ? `/${unitMinimum}` : null}
								</span>
							)
						}
						componentProps={{ key: 'unit-count' }}
					/>
					<Button
						className={cn(
							'expand-collapse-btn icon-only',
							{
								'icon-remove': !collapsed,
								'icon-keyboard-arrow-down': collapsed,
							},
						)}
						quickTypes={QuickTypes.Secondary}
						display={Display.Text}
						sizes={Sizes.Small}
						onClick={() => setCollapsed(id)}
						buttonProps={{
							'aria-label': 'Collapse Accordion',
						}}
					/>
				</span>
			</div>
			{description}
			{errorMessage}
			<div className="child-list">
				{
					items?.length
						? items.map((item, index) => renderItem(item, index))
						: (
							<div className="empty-state">
								<p>{emptyLabel}</p>
							</div>
						)
				}
			</div>
		</div>
	);
};

export default CourseAccordion;
